import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Paper, Button, Grid, Chip, CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Add, Edit, Business, LocationOn, Delete } from '@mui/icons-material';
import api from '../api/axios';
import toast from 'react-hot-toast';

const Enterprises = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchEnterprises();
    const interval = setInterval(() => {
      if (localStorage.getItem("entNeedRefresh") === "true") {
        localStorage.removeItem("entNeedRefresh");
        fetchEnterprises();
      }
    }, 500);
    return () => clearInterval(interval);
  }, [location]);

  const fetchEnterprises = async () => {
    try {
      const res = await api.get('/api/v1/enterprises/');
      setEnterprises(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Failed to load enterprises');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (ent) => {
    try {
      await api.patch(`/api/v1/enterprises/${ent.id}`, { is_active: !ent.is_active });
      toast.success(ent.is_active ? 'Enterprise deactivated' : 'Enterprise activated');
      fetchEnterprises();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      await api.delete(`/api/v1/enterprises/${deleteDialog.id}`);
      toast.success('Enterprise deleted');
      fetchEnterprises();
    } catch (err) {
      toast.error('Failed to delete enterprise');
    } finally {
      setDeleting(false);
      setDeleteDialog(null);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600} color="#2c3e50">🏢 Enterprises</Typography>
        <Button onClick={() => navigate('/enterprises/new')} variant="contained" startIcon={<Add />} sx={{ bgcolor: '#1abc9c', '&:hover': { bgcolor: '#16a085' } }}>
          Add New
        </Button>
      </Box>

      {enterprises.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">No enterprises yet.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {enterprises.map(ent => (
            <Grid item xs={12} key={ent.id}>
              <Paper sx={{ p: 3, borderRadius: '10px', border: '1px solid #e0e0e0', opacity: ent.is_active ? 1 : 0.6, '&:hover': { borderColor: '#1abc9c', boxShadow: '0 2px 8px rgba(26,188,156,0.15)' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={600} color="#2c3e50">
                      <Business sx={{ mr: 1, color: '#1abc9c' }} />
                      {ent.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Chip icon={<LocationOn />} label={[ent.city, ent.country].filter(Boolean).join(', ') || 'N/A'} size="small" variant="outlined" />
                      {ent.rcs && <Chip label={`RCS: ${ent.rcs}`} size="small" variant="outlined" />}
                      {ent.tva && <Chip label={`TVA: ${ent.tva}`} size="small" variant="outlined" />}
                      <Chip label={ent.is_active ? 'Active' : 'Inactive'} size="small" color={ent.is_active ? 'success' : 'error'} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Created: {new Date(ent.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={() => navigate(`/enterprises/${ent.id}/edit`)} size="small" variant="outlined" startIcon={<Edit />}>Edit</Button>
                    <Button onClick={() => handleToggleStatus(ent)} size="small" variant="outlined" color={ent.is_active ? 'error' : 'success'}>
                      {ent.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button onClick={() => setDeleteDialog(ent)} size="small" variant="outlined" color="error">
                      <Delete fontSize="small" />
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>Delete Enterprise</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{deleteDialog?.name}"</strong>?
            This will permanently delete the enterprise and all associated users and data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Enterprises;
